import { useState, useEffect, useCallback, useRef } from 'react';
import { preferencesService } from '../../services/api';
import { userKey } from '../../utils/userStorage';

const STORAGE_KEY = 'nizus_dashboard_prefs';

// Clés "niveau" : leur valeur dépend des orders côté server (achats, packs, écoles).
// → Le server est seule source de vérité. Le local ne doit JAMAIS contenir ces clés
//   pour éviter qu'elles soient re-poussées au server (bug "addedLevels qui réapparaît").
//
// IMPORTANT : `lastLevel` n'est PAS dans cette liste car c'est une simple UI pref (dernier
// niveau visité), pas une donnée validée côté server. Le stocker en local est nécessaire
// pour que DashboardRedirect (App.jsx) puisse le lire AVANT le mount du hook lors d'un
// retour sur /dashboard depuis une autre route (ex. /dashboard/account).
const SERVER_AUTHORITATIVE_KEYS = new Set([
    'hiddenLevels',
    'addedLevels',
    'sidebarOrder',
]);

const DEFAULT_PREFS = {
    lastLevel: null,
    lastTabs: {},
    sidebarOrder: [],
    addedLevels: [],
    hiddenLevels: [],
    lastCategories: {},
    lastCourseModules: {},
    lastCourseGroups: {},
    // Admin-specific (independent from user dashboard)
    adminSidebarOrder: [],
    adminLastLevel: null,
    adminActiveSection: 'vu-examen',
};

// Strip les clés server-authoritative d'un objet prefs.
function stripServerKeys(obj) {
    if (!obj) return obj;
    const result = {};
    for (const key of Object.keys(obj)) {
        if (!SERVER_AUTHORITATIVE_KEYS.has(key)) {
            result[key] = obj[key];
        }
    }
    return result;
}

// Deep merge helper for nested preference objects
function deepMergePrefs(base, overlay) {
    const result = { ...base };
    for (const key of Object.keys(overlay)) {
        const baseVal = base[key];
        const overlayVal = overlay[key];
        // Deep merge plain objects (like lastTabs, lastCategories, etc.)
        if (
            overlayVal !== null &&
            typeof overlayVal === 'object' &&
            !Array.isArray(overlayVal) &&
            baseVal !== null &&
            typeof baseVal === 'object' &&
            !Array.isArray(baseVal)
        ) {
            result[key] = { ...baseVal, ...overlayVal };
        } else if (overlayVal !== undefined) {
            result[key] = overlayVal;
        }
    }
    return result;
}

// loadFromStorage : retourne TOUJOURS une version safe (sans clés server-authoritative).
// Comme ça, AUCUN code consommateur ne peut accidentellement re-pousser du stale au server.
function loadFromStorage() {
    try {
        const raw = localStorage.getItem(userKey(STORAGE_KEY));
        if (!raw) return null;
        return stripServerKeys(JSON.parse(raw));
    } catch {
        return null;
    }
}

// Merge defaults + optional server + optional local into final prefs.
// - Clés niveau (SERVER_AUTHORITATIVE_KEYS) : viennent UNIQUEMENT de serverPrefs (local est strippé)
// - Autres clés (interaction state) : local override server (UX continue)
function buildPrefs(serverPrefs, local) {
    let base = { ...DEFAULT_PREFS };
    if (serverPrefs) base = deepMergePrefs(base, serverPrefs);
    if (local) base = deepMergePrefs(base, local);  // local déjà strippé via loadFromStorage
    return base;
}

// saveToStorage : strip les clés server-authoritative avant d'écrire localStorage.
// Le localStorage ne sert qu'à mettre en cache les prefs d'interaction (lastTabs, etc.).
function saveToStorage(prefs) {
    try {
        const safe = stripServerKeys(prefs);
        localStorage.setItem(userKey(STORAGE_KEY), JSON.stringify(safe));
    } catch { /* quota exceeded or private browsing */ }
}

const useDashboardPrefs = (serverPrefs = null) => {
    const [prefs, setPrefs] = useState(() => {
        const local = loadFromStorage();
        const merged = buildPrefs(serverPrefs, local);
        // If new device (no local) and server prefs exist, seed localStorage
        if (!local && serverPrefs) saveToStorage(merged);
        return merged;
    });

    const syncTimeoutRef = useRef(null);
    const hasHydratedRef = useRef(!!serverPrefs);

    // Hydrate from server prefs UNE SEULE FOIS (premier non-null serverPrefs).
    // Raison : si on re-hydrate à chaque changement de user.dashboardPrefs, un re-fetch
    // /user.php déclenché AVANT que le sync debounce ait fini d'écrire en DB ramène les
    // anciennes valeurs et écrase les modifs locales (bug "le niveau caché revient au click").
    // Après hydratation initiale, le state local est la source de vérité ; syncToServer
    // pousse les modifs vers la DB en arrière-plan.
    useEffect(() => {
        if (serverPrefs && !hasHydratedRef.current) {
            hasHydratedRef.current = true;
            setPrefs(() => {
                const freshLocal = loadFromStorage();
                const next = buildPrefs(serverPrefs, freshLocal);
                saveToStorage(next);
                return next;
            });
        }
    }, [serverPrefs]);

    // Debounced background sync to DB (1s). On garde la dernière payload pending dans un ref
    // pour pouvoir la flusher au unmount (sinon bug : logout dans la fenêtre de 1s du debounce
    // → setLastLevel('ppl') jamais envoyé en DB → user revient sur PSTAR à la reconnexion car
    // lastLevel est server-authoritative et le local est strippé).
    const pendingPayloadRef = useRef(null);
    const syncToServer = useCallback((newPrefs) => {
        pendingPayloadRef.current = newPrefs;
        if (syncTimeoutRef.current) {
            clearTimeout(syncTimeoutRef.current);
        }
        syncTimeoutRef.current = setTimeout(() => {
            const payload = pendingPayloadRef.current;
            pendingPayloadRef.current = null;
            syncTimeoutRef.current = null;
            preferencesService.save(payload).catch(err => {
                console.error('Failed to sync prefs to server:', err);
            });
        }, 1000);
    }, []);

    // Cleanup au unmount : si un sync est encore pending (debounce pas écoulé), le flusher
    // immédiatement au lieu de l'annuler. Couvre le cas logout / navigation away rapide.
    // Le .catch silencieux est volontaire : si la session est déjà clear côté serveur,
    // l'appel échouera proprement sans casser le logout en cours.
    useEffect(() => {
        return () => {
            if (syncTimeoutRef.current) {
                clearTimeout(syncTimeoutRef.current);
                syncTimeoutRef.current = null;
                if (pendingPayloadRef.current) {
                    const payload = pendingPayloadRef.current;
                    pendingPayloadRef.current = null;
                    preferencesService.save(payload).catch(() => {});
                }
            }
        };
    }, []);

    const updatePrefs = useCallback((partial) => {
        setPrefs((prev) => {
            // MERGE prev (state React, contient server-keys) avec localStorage (interaction state
            // mis à jour par d'autres hooks comme useLevelOverview/CoursesTab qui écrivent direct).
            // Si on faisait `loadFromStorage() || prev`, on perdrait les server-keys de prev car
            // loadFromStorage les strippe → bug : 3 setters successifs perdaient mutuellement leurs keys.
            const local = loadFromStorage() || {};
            const next = { ...prev, ...local, ...partial };
            saveToStorage(next);
            syncToServer(next);
            return next;
        });
    }, [syncToServer]);

    const setLastLevel = useCallback((levelId) => {
        updatePrefs({ lastLevel: levelId });
    }, [updatePrefs]);

    const setTabForLevel = useCallback((levelId, tab) => {
        setPrefs((prev) => {
            const local = loadFromStorage() || {};
            const merged = { ...prev, ...local };
            const next = {
                ...merged,
                lastTabs: { ...merged.lastTabs, [levelId]: tab }
            };
            saveToStorage(next);
            syncToServer(next);
            return next;
        });
    }, [syncToServer]);

    const getTabForLevel = useCallback((levelId) => {
        const fresh = loadFromStorage();
        return fresh?.lastTabs?.[levelId] || null;
    }, []);

    const setSidebarOrder = useCallback((order) => {
        updatePrefs({ sidebarOrder: order });
    }, [updatePrefs]);

    const setAddedLevels = useCallback((added) => {
        updatePrefs({ addedLevels: added });
    }, [updatePrefs]);

    const setHiddenLevels = useCallback((hidden) => {
        updatePrefs({ hiddenLevels: hidden });
    }, [updatePrefs]);

    const setCategoryForLevel = useCallback((levelId, category) => {
        setPrefs((prev) => {
            const local = loadFromStorage() || {};
            const merged = { ...prev, ...local };
            const next = { ...merged, lastCategories: { ...merged.lastCategories, [levelId]: category } };
            saveToStorage(next);
            syncToServer(next);
            return next;
        });
    }, [syncToServer]);

    const setCourseModuleForLevel = useCallback((levelId, moduleId) => {
        setPrefs((prev) => {
            const local = loadFromStorage() || {};
            const merged = { ...prev, ...local };
            const next = { ...merged, lastCourseModules: { ...merged.lastCourseModules, [levelId]: moduleId } };
            saveToStorage(next);
            syncToServer(next);
            return next;
        });
    }, [syncToServer]);

    const setCourseGroupForModule = useCallback((moduleId, groupName) => {
        setPrefs((prev) => {
            const local = loadFromStorage() || {};
            const merged = { ...prev, ...local };
            const next = { ...merged, lastCourseGroups: { ...merged.lastCourseGroups, [String(moduleId)]: groupName } };
            saveToStorage(next);
            syncToServer(next);
            return next;
        });
    }, [syncToServer]);

    const setAdminSidebarOrder = useCallback((order) => {
        updatePrefs({ adminSidebarOrder: order });
    }, [updatePrefs]);

    const setAdminLastLevel = useCallback((levelVar) => {
        updatePrefs({ adminLastLevel: levelVar });
    }, [updatePrefs]);

    const setAdminActiveSection = useCallback((section) => {
        updatePrefs({ adminActiveSection: section });
    }, [updatePrefs]);

    // Flush synchrone du sync debounced en cours. À appeler AVANT un logout / une navigation
    // qui pourrait clear la session côté serveur — sinon le sync part en parallèle du logout
    // et peut être rejeté en 401 (race condition).
    const flushPrefs = useCallback(async () => {
        if (syncTimeoutRef.current) {
            clearTimeout(syncTimeoutRef.current);
            syncTimeoutRef.current = null;
        }
        if (pendingPayloadRef.current) {
            const payload = pendingPayloadRef.current;
            pendingPayloadRef.current = null;
            try {
                await preferencesService.save(payload);
            } catch (err) {
                console.error('Failed to flush prefs before logout:', err);
            }
        }
    }, []);

    return {
        prefs,
        setLastLevel,
        setTabForLevel,
        getTabForLevel,
        setSidebarOrder,
        setAddedLevels,
        setHiddenLevels,
        setCategoryForLevel,
        setCourseModuleForLevel,
        setCourseGroupForModule,
        updatePrefs,
        setAdminSidebarOrder,
        setAdminLastLevel,
        setAdminActiveSection,
        flushPrefs,
    };
};

export default useDashboardPrefs;
